import React, { useState, useEffect } from 'react';
import { Employee } from '../types';
import { X, User, Briefcase, DollarSign, CreditCard, Save, Calendar } from 'lucide-react';

interface EmployeeFormProps {
  initialData?: any;
  onClose: () => void;
  onSave: (data: any) => void;
}

const EmployeeForm: React.FC<EmployeeFormProps> = ({ initialData, onClose, onSave }) => {
  const [formData, setFormData] = useState<Partial<Employee>>({
    fullName: '',
    civilId: '',
    position: '',
    baseSalary: 0,
    status: 'Active',
    salaryDate: 1
  });

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const inputClass = "w-full bg-slate-50 border-2 border-slate-100 rounded-xl py-3 px-4 outline-none focus:border-fly-500 transition-all text-sm font-medium";
  const labelClass = "block text-xs font-black text-slate-500 mb-2 mr-1 uppercase tracking-wider";

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden my-8">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-fly-50 p-2.5 rounded-xl">
              <User className="text-fly-600" size={20} />
            </div>
            <h2 className="text-xl font-black text-slate-800">{initialData ? 'تعديل بيانات موظف' : 'إضافة موظف جديد'}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
            <X size={20} className="text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6">
          <div className="space-y-6">
            <div>
              <label className={labelClass}>الإسم الكامل *</label>
              <div className="relative">
                <User className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input 
                  required
                  className={`${inputClass} pr-12`}
                  value={formData.fullName}
                  onChange={e => setFormData({ ...formData, fullName: e.target.value })}
                  placeholder="أدخل الإسم الثلاثي"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>الرقم المدني</label>
                <div className="relative">
                  <CreditCard className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input 
                    className={`${inputClass} pr-12 font-mono text-left`}
                    dir="ltr"
                    value={formData.civilId}
                    onChange={e => setFormData({ ...formData, civilId: e.target.value })}
                    placeholder="290XXXXXXXXX"
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>المسمى الوظيفي</label>
                <div className="relative">
                  <Briefcase className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input 
                    className={`${inputClass} pr-12`}
                    value={formData.position}
                    onChange={e => setFormData({ ...formData, position: e.target.value })}
                    placeholder="مثال: محصل، محاسب"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>الراتب الأساسي (KWD)</label>
                <div className="relative">
                  <DollarSign className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input 
                    type="number"
                    min="0"
                    step="0.001"
                    className={`${inputClass} pr-12 font-black`}
                    value={formData.baseSalary}
                    onChange={e => setFormData({ ...formData, baseSalary: Number(e.target.value) })}
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>تاريخ استحقاق الراتب (يوم)</label>
                <div className="relative">
                  <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input 
                    type="number"
                    min="1"
                    max="31"
                    className={`${inputClass} pr-12`}
                    value={formData.salaryDate}
                    onChange={e => setFormData({ ...formData, salaryDate: Number(e.target.value) })}
                    placeholder="يوم صرف الراتب مثال: 1"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>حالة الموظف</label>
                <select 
                  className={inputClass}
                  value={formData.status}
                  onChange={e => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="Active">على رأس العمل</option>
                  <option value="On Leave">في إجازة</option>
                  <option value="Resigned">مستقيل</option>
                </select>
              </div>
              <div></div>
            </div>
          </div>
        </form>

        <div className="p-4 sm:p-6 bg-slate-50 border-t border-slate-100 flex flex-wrap sm:flex-nowrap gap-3 shrink-0">
          <button
            type="submit"
            onClick={handleSubmit}
            className="w-full sm:flex-1 bg-fly-600 text-white py-3.5 sm:py-4 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-fly-700 transition-all shadow-lg shadow-fly-600/20 active:scale-95 text-sm"
          >
            <Save size={18} />
            حفظ البيانات
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-8 bg-white text-slate-500 py-3.5 sm:py-4 rounded-2xl font-bold border-2 border-slate-200 hover:bg-slate-50 transition-all active:scale-95 text-sm"
          >
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeForm;
